// Mitarbeiter-Aufgaben-Banner — zuoberst im Terminkalender (bei den Geburtstagen).
// Zeigt fällige Aufgaben pro Standort: ROT solange offen, GRÜN nach Bestätigung (heute).
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ClipboardList, CheckCircle2, AlertCircle, Settings, RotateCcw, Loader2, MapPin, CalendarClock,
} from 'lucide-react';
import StaffTasksManageDialog, { frequencyText } from './StaffTasksManageDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const StaffTasksBanner = ({ studioFilter = 'all', studios = [] }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff-tasks/due`, { params: { studio_id: studioFilter || 'all' } });
      setTasks(res.data || []);
    } catch (e) {
      // Still fail — Banner bleibt einfach leer
    } finally {
      setLoading(false);
    }
  }, [studioFilter]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (task) => {
    setBusyId(task.id);
    try {
      await axios.post(`${API}/staff-tasks/${task.id}/complete`);
      toast.success(`„${task.title}" als erledigt bestätigt`);
      await load();
    } catch (e) {
      toast.error('Bestätigen fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const undo = async (task) => {
    setBusyId(task.id);
    try {
      await axios.post(`${API}/staff-tasks/${task.id}/uncomplete`);
      toast.info(`„${task.title}" wieder offen`);
      await load();
    } catch (e) {
      toast.error('Rückgängig fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;

  const openTasks = tasks.filter((t) => t.status === 'open');
  const showAllStudios = (studioFilter === 'all' || !studioFilter);

  // Keine Aufgaben: für Admins eine schlanke Leiste zum Verwalten, sonst nichts.
  if (tasks.length === 0) {
    if (!isAdmin) return null;
    return (
      <>
        <div
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2"
          data-testid="staff-tasks-banner-empty"
        >
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Keine fälligen Aufgaben
          </span>
          <Button variant="ghost" size="sm" onClick={() => setManageOpen(true)} data-testid="staff-tasks-manage-btn">
            <Settings className="w-4 h-4 mr-1" /> Aufgaben verwalten
          </Button>
        </div>
        <StaffTasksManageDialog open={manageOpen} onClose={() => setManageOpen(false)} studios={studios} onChanged={load} />
      </>
    );
  }

  const TaskCard = ({ t }) => {
    const isOpen = t.status === 'open';
    return (
      <div
        className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
          isOpen ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'
        }`}
        data-testid={`staff-task-card-${t.id}`}
      >
        <div className="flex items-start gap-2 min-w-0">
          {isOpen
            ? <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            : <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <p className={`font-semibold ${isOpen ? 'text-red-800' : 'text-green-800'}`}>{t.title}</p>
            {t.description && <p className="text-xs text-gray-600 mt-0.5">{t.description}</p>}
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              {showAllStudios && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.studio_name}</span>
              )}
              <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />{frequencyText(t)}</span>
              {!isOpen && t.completed_by_name && (
                <span className="text-green-700">erledigt von {t.completed_by_name} · {fmtTime(t.completed_at)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="shrink-0">
          {isOpen ? (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => confirm(t)}
              disabled={busyId === t.id}
              data-testid={`staff-task-confirm-${t.id}`}
            >
              {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Erledigt
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="text-green-700 hover:text-green-900"
              onClick={() => undo(t)}
              disabled={busyId === t.id}
              data-testid={`staff-task-undo-${t.id}`}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Rückgängig
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Gruppierung nach Standort, wenn "Alle Standorte"
  const groups = {};
  if (showAllStudios) {
    tasks.forEach((t) => {
      (groups[t.studio_name] = groups[t.studio_name] || []).push(t);
    });
  }

  return (
    <>
      <div
        className={`rounded-lg border-2 shadow-md p-4 ${
          openTasks.length > 0 ? 'border-red-300 bg-gradient-to-r from-red-50 to-orange-50' : 'border-green-300 bg-green-50'
        }`}
        data-testid="staff-tasks-banner"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-red-600" />
            Aufgaben
            {openTasks.length > 0 && (
              <span className="text-xs font-semibold bg-red-600 text-white rounded-full px-2 py-0.5" data-testid="staff-tasks-open-count">
                {openTasks.length} offen
              </span>
            )}
          </h3>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setManageOpen(true)} data-testid="staff-tasks-manage-btn">
              <Settings className="w-4 h-4 mr-1" /> Verwalten
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {showAllStudios ? (
            Object.keys(groups).sort().map((studioName) => (
              <div key={studioName} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {studioName}
                </p>
                {groups[studioName].map((t) => <TaskCard key={t.id} t={t} />)}
              </div>
            ))
          ) : (
            tasks.map((t) => <TaskCard key={t.id} t={t} />)
          )}
        </div>
      </div>
      <StaffTasksManageDialog open={manageOpen} onClose={() => setManageOpen(false)} studios={studios} onChanged={load} />
    </>
  );
};

export default StaffTasksBanner;
